import { useEffect, useState, type ReactNode } from 'react'
import { type AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser'
import { getMsalInstance, API_SCOPES } from './msalConfig'
import { getRolesFromAccessToken, rolesFrom, type Roles } from './roles'
import { DEV_ROLES_HEADER, readDevRoles } from './devRoles'
import { AuthContext, type AuthState } from './authContext'

/**
 * The token's roles, or the dev picker's. As in the API, only someone the
 * token already lets in may pick, so the no-access screen stays truthful.
 */
function rolesFor(accessToken: string): Roles {
  const fromToken = getRolesFromAccessToken(accessToken)
  const picked = readDevRoles()
  return picked && fromToken.isReader ? rolesFrom(picked) : fromToken
}

/**
 * What Entra said, for the sign-in screen. MSAL errors carry the AADSTS
 * text in `errorMessage`; anything else falls back to the message.
 */
function describe(err: unknown): string {
  if (err && typeof err === 'object' && 'errorMessage' in err) {
    const m = (err as { errorMessage: unknown }).errorMessage
    if (typeof m === 'string' && m !== '') return m
  }
  return err instanceof Error && err.message !== '' ? err.message : 'Sign-in failed.'
}

/**
 * Entra ID sign-in. This is the real provider and the only one that survives
 * a production build; see `AuthProvider` for how the local bypass is chosen
 * and why it cannot reach a deployed bundle.
 */
export function EntraAuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({ status: 'initialising' })

  useEffect(() => {
    // initialize() + handleRedirectPromise() must complete before we read any
    // account state. After that we try, in order:
    //   1. A cached account from sessionStorage (a prior page load or the
    //      redirect-result we just processed).
    //   2. Silent SSO against the user's existing Entra tenant session
    //      (e.g. they're already signed in to Outlook / SharePoint / Teams
    //      in this browser). If the browser has the session cookie or the
    //      Microsoft SSO extension, this returns a fresh account with no UI.
    //   3. If both fail, transition to `unauthenticated` so the sign-in
    //      button renders.
    //
    // ssoSilent failure modes are deliberately swallowed: there's no useful
    // signal for the user beyond "you'll need to click sign in." The most
    // common failure paths are no tenant session in the browser, third-
    // party-cookie restrictions (Safari ITP), and Conditional Access policies
    // that require a fresh interactive prompt.
    let cancelled = false

    async function bootAuth(): Promise<void> {
      const msalInstance = getMsalInstance()
      await msalInstance.initialize()
      await msalInstance.handleRedirectPromise()
      if (cancelled) return

      // Path 1: cached account.
      const cached = msalInstance.getAllAccounts()[0]
      if (cached) {
        msalInstance.setActiveAccount(cached)
        await acquireAndSetAuthenticated(cached)
        return
      }

      // Path 2: silent SSO using the existing tenant session in the browser.
      // The result already includes the access token we need — decode roles
      // directly to avoid a second round trip.
      try {
        const ssoResult = await msalInstance.ssoSilent({ scopes: API_SCOPES })
        if (cancelled) return
        if (ssoResult.account) {
          msalInstance.setActiveAccount(ssoResult.account)
          const roles = rolesFor(ssoResult.accessToken)
          setAuthState({ status: 'authenticated', account: ssoResult.account, roles })
          return
        }
      } catch {
        // ssoSilent failed (no session, interaction required, blocked
        // cookies). Quietly fall through to unauthenticated; the user
        // clicks Sign in.
      }
      if (cancelled) return

      setAuthState({ status: 'unauthenticated' })
    }

    async function acquireAndSetAuthenticated(account: AccountInfo): Promise<void> {
      const msalInstance = getMsalInstance()
      try {
        const result = await msalInstance.acquireTokenSilent({
          scopes: API_SCOPES,
          account,
        })
        if (cancelled) return
        const roles = rolesFor(result.accessToken)
        setAuthState({ status: 'authenticated', account, roles })
      } catch (err) {
        if (cancelled) return
        if (err instanceof InteractionRequiredAuthError) {
          // Silent acquire failed — recover via redirect. The redirect
          // navigates away; the next page load will re-enter this effect.
          await msalInstance.acquireTokenRedirect({ scopes: API_SCOPES, account })
          return
        }
        // Some other failure (network, configuration). Surface as
        // unauthenticated, with the reason, so the user can retry sign-in
        // or report what Entra said.
        setAuthState({ status: 'unauthenticated', reason: describe(err) })
      }
    }

    // Without this catch, any throw inside bootAuth (most commonly an
    // error coming back in the redirect hash from a failed/denied
    // acquireTokenRedirect) leaves authState stuck on 'initialising'
    // and the SPA shows STARTING… forever. Surfacing as 'unauthenticated'
    // gives the user a Sign-in button to retry, and the reason tells them
    // why the last attempt was refused: a new joiner who is not yet
    // assigned to the application otherwise sees only a sign-in loop.
    bootAuth().catch(err => {
      if (cancelled) return
      // eslint-disable-next-line no-console
      console.error('Auth boot failed:', err)
      setAuthState({ status: 'unauthenticated', reason: describe(err) })
    })

    return () => {
      cancelled = true
    }
  }, [])

  const signIn = async () => {
    await getMsalInstance().loginRedirect({ scopes: API_SCOPES })
  }

  const signOut = async () => {
    const account = authState.status === 'authenticated' ? authState.account : null
    setAuthState({ status: 'unauthenticated' })
    await getMsalInstance().logoutRedirect({ account })
  }

  const getToken = async (): Promise<string> => {
    const account = authState.status === 'authenticated' ? authState.account : undefined
    if (!account) throw new Error('Not authenticated')

    const msalInstance = getMsalInstance()
    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: API_SCOPES,
        account,
      })
      return result.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Silent acquisition failed (expired refresh token, Conditional Access
        // policy change, etc.). Kick off an interactive redirect to recover.
        await msalInstance.acquireTokenRedirect({ scopes: API_SCOPES, account })
        // acquireTokenRedirect navigates away; this line is never reached.
        throw new Error('Redirecting for interactive auth')
      }
      throw err
    }
  }

  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = { Authorization: `Bearer ${await getToken()}` }
    const picked = readDevRoles()
    if (picked) headers[DEV_ROLES_HEADER] = picked.join(',')
    return headers
  }

  return (
    <AuthContext.Provider value={{ authState, signIn, signOut, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  )
}
